import Sentry from '@/sentry'
import { proxy, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import { clearStore } from '@/lib/valtio/reset-store'
import { useStoreKeySelector } from '@/lib/valtio/store-hooks'

import { parseSubTimerRunSnapshot } from '~shared/rundown/sub-timer-run'
import type { SubTimerRunWithId } from '~shared/rundown/sub-timer-run'

import type { DocumentChange } from 'firebase/firestore'

type SubTimerRuns = Record<string, SubTimerRunWithId>

/**
 * Running state of sub timers on the current runner, keyed by timer id.
 *
 * @remarks
 *   Mirrors `runners/{runnerId}/sub-timers`. As with the cue store, the
 *   Firestore listener is the only writer: actions go to the server, and the
 *   change arrives back through the listener. Entries are replaced, never
 *   mutated, so each timer's key subscription sees every change.
 */
const subTimerRunsStore = proxy<SubTimerRuns>({})

export const subTimerRunsActions = {
  processDocChanges(changes: DocumentChange[]) {
    changes.forEach((change) => {
      if (change.type === 'removed') {
        delete subTimerRunsStore[change.doc.id]
        return
      }
      try {
        subTimerRunsStore[change.doc.id] = parseSubTimerRunSnapshot(change.doc)
      } catch (error) {
        Sentry.captureException(error)
      }
    })
  },

  /** Called when the runner changes or the rundown is left. */
  resetStore() {
    clearStore(subTimerRunsStore)
  },
}

/** One timer's run. Re-renders only when that timer's doc changes. */
export function useSubTimerRun(timerId: string): SubTimerRunWithId | null {
  return useStoreKeySelector(subTimerRunsStore, timerId, (run) => run ?? null)
}

/**
 * Every run on the runner, for views that show timers from many cues (outputs).
 * Tracks the keys read, so it re-renders only when one of those runs changes.
 */
export function useSubTimerRuns(): Readonly<SubTimerRuns> {
  return useSnapshot(subTimerRunsStore)
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(subTimerRunsStore, { name: 'subTimerRunsStore', enabled: true })
}
// #endregion
